import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { MessageCircle, AlertCircle } from "lucide-react";
import { Progress } from "@shared/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import { useMonthlySupportSummary } from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface ComplaintSummaryProps {
  timeRange: "day" | "week" | "month" | "quarter" | "year";
  userRole: "manager" | "executive" | "callcenter" | "recovery";
}

interface ComplaintCategoryProps {
  category: string;
  count: number;
  percentage: number;
  trend: "up" | "down" | "neutral";
}

function ComplaintCategory({ category, count, percentage, trend }: ComplaintCategoryProps) {

  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="card-subheading-text truncate">{category}</span>
        <div className="flex items-center gap-1">
          <span className="text-[10px]">{count}</span>
          <span className={`text-[10px] ${
            trend === 'up' ? 'text-red-500' : 
            trend === 'down' ? 'text-green-500' : 'text-gray-500'
          }`}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
          </span>
        </div>
      </div>
      <Progress value={percentage} className="h-1" />
    </div>
  );
}

export function ComplaintSummary({ timeRange, userRole }: ComplaintSummaryProps) {
    const remoteUtilityId = getRemoteUtilityId();
  const { 
    data: supportData, 
    isLoading: isSupportLoading, 
    error: supportError 
  } = useMonthlySupportSummary({
    remote_utility_id: remoteUtilityId,
  });

  // Transform API data to complaint data
  const complaintData = supportData?.result ? {
    activeComplaints: supportData.result.complaints.active,
    resolvedToday: supportData.result.complaints.resolvedToday,
    escalationRate: supportData.result.complaints.avgCompletionTimeHr,
    resolutionRate: `${supportData.result.complaints.resolutionPercent}%`,
    categories: transformComplaintCategories(supportData.result.supportRequestTypeCounts?.Complaint || {}),
    severity: transformComplaintSeverity(supportData.result.priorityCounts?.Complaint || {})
  } : {
    // Fallback data if API is not available
    activeComplaints: 0,
    resolvedToday: 0,
    escalationRate: "0%",
    resolutionRate: "0%",
    categories: [],
    severity: [
      { level: "Critical", value: 0 },
      { level: "High", value: 0 },
      { level: "Medium", value: 0 },
      { level: "Low", value: 0 }
    ]
  };

  // Transform complaint categories from API response (top 5 only)
  function transformComplaintCategories(complaintTypes: Record<string, number>) {
    const entries = Object.entries(complaintTypes);
    const totalCount = entries.reduce((sum, [, count]) => sum + count, 0);
    
    // Sort by count and take top 5
    const sortedEntries = entries
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return sortedEntries.map(([category, count]) => ({
      category: category.length > 20 ? category.substring(0, 17) + "..." : category,
      count,
      percentage: totalCount > 0 ? Math.round((count / totalCount) * 100) : 0,
      trend: "neutral" as const // Could be enhanced with historical data
    }));
  }

  // Transform complaint severity data from API response
  function transformComplaintSeverity(priorityCounts: Record<string, number>) {
    const totalCount = Object.values(priorityCounts).reduce((sum, count) => sum + count, 0);
    
    if (totalCount === 0) {
      return [
        { level: "Critical", value: 0 },
        { level: "High", value: 0 },
        { level: "Medium", value: 0 },
        { level: "Low", value: 0 }
      ];
    }

    // Map API priority levels to our severity levels
    const critical = priorityCounts["Critical"] || priorityCounts["Emergency"] || 0;
    const high = priorityCounts["High"] || 0;
    const medium = priorityCounts["Medium"] || 0;
    const low = priorityCounts["Low"] || priorityCounts["Normal"] || 0;

    return [
      { level: "Critical", value: Math.round((critical / totalCount) * 100) },
      { level: "High", value: Math.round((high / totalCount) * 100) },
      { level: "Medium", value: Math.round((medium / totalCount) * 100) },
      { level: "Low", value: Math.round((low / totalCount) * 100) }
    ];
  }

  // Loading state
  if (isSupportLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="card-heading-text flex items-center justify-between">
            Complaints
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-muted-foreground">Loading complaints...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (supportError) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-1 px-4 pt-3">
          <CardTitle className="card-heading-text flex items-center justify-between">
            Complaints
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-red-500">Failed to load complaints</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="card-heading-text flex items-center justify-between">
          Complaints
          <MessageCircle className="h-4 w-4 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col items-center justify-center p-1.5 rounded border">
            <span className="card-subheading-text text-muted-foreground">Active Complaints</span>
            <span className="card-value-text font-bold text-amber-600">{complaintData.activeComplaints}</span>
          </div>
          <div className="flex flex-col items-center justify-center p-1.5 rounded border">
            <span className="card-subheading-text text-muted-foreground">Resolved Today</span>
            <span className="card-value-text font-bold text-green-600">{complaintData.resolvedToday}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col items-center p-1.5 rounded border">
            <span className="card-subheading-text text-muted-foreground">Escalation Rate</span>
            <span className="card-value-text font-bold">{complaintData.escalationRate}</span>
          </div>
          <div className="flex flex-col items-center p-1.5 rounded border">
            <span className="card-subheading-text text-muted-foreground">Resolution Rate</span>
            <span className="card-value-text font-bold">{complaintData.resolutionRate}</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <h3 className="card-subheading-text font-medium text-muted-foreground">Top Categories</h3>
          <div className="space-y-2">
            {complaintData.categories.map((category) => (
              <ComplaintCategory key={category.category} {...category} />
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <h3 className="card-subheading-text font-medium text-muted-foreground">By Severity</h3>
          <TooltipProvider>
            <div className="flex items-center gap-0.5 h-3">
              {complaintData.severity.map((level, index) => (
                <Tooltip key={level.level}>
                  <TooltipTrigger asChild>
                    <div 
                      style={{ width: `${level.value}%` }} 
                      className={`h-full rounded ${
                        index === 0 ? 'bg-red-500' : 
                        index === 1 ? 'bg-amber-500' : 
                        index === 2 ? 'bg-blue-400' : 'bg-green-400'
                      }`}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="card-subheading-text" side="top">
                    {level.level}: {level.value}%
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>Critical</span>
            <span>High</span>
            <span>Medium</span>
            <span>Low</span>
          </div>
        </div>
        
       
      </CardContent>
    </Card>
  );
}